/**
 * Google タグ（GTM 等）の生 HTML を公開ページの DOM に注入するユーティリティ。
 *
 * 重要: innerHTML でセットした <script> はブラウザに実行されない。GTM の head
 * スニペットはインライン <script> を含むため、script 要素を作り直して append する。
 *
 * 注入対象は公開ページ（ランディング / 公開フォーム）のコンポーネントのみ。
 * 管理画面では呼ばない（管理操作を計測しないため）。
 */
import { useEffect } from 'react';

const MARKER_ATTR = 'data-goenhub-gtag';

/** key に紐づく既存の注入ノードをすべて除去する。 */
function removeInjected(key: string): void {
  document
    .querySelectorAll(`[${MARKER_ATTR}="${key}"]`)
    .forEach((node) => node.remove());
}

/**
 * <script> 要素は textContent や src を引き継いで再生成しないと実行されない。
 * その他のノード（<noscript> 等）はそのまま import すれば良い。
 */
function materializeNode(node: Node, key: string): Node {
  if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'SCRIPT') {
    const src = node as HTMLScriptElement;
    const script = document.createElement('script');
    for (const attr of Array.from(src.attributes)) {
      script.setAttribute(attr.name, attr.value);
    }
    script.text = src.textContent ?? '';
    script.setAttribute(MARKER_ATTR, key);
    return script;
  }
  const clone = node.cloneNode(true);
  if (clone.nodeType === Node.ELEMENT_NODE) {
    (clone as Element).setAttribute(MARKER_ATTR, key);
  }
  return clone;
}

/**
 * html をパースして target（head 先頭 / body 直下）に注入する。
 * 同一 key の既存ノードは事前に除去するため冪等。
 */
export function injectTagHtml(
  html: string,
  target: 'head' | 'body',
  key: string,
): void {
  removeInjected(key);
  const trimmed = html.trim();
  if (!trimmed) return;

  const template = document.createElement('template');
  template.innerHTML = trimmed;
  const nodes = Array.from(template.content.childNodes).map((n) =>
    materializeNode(n, key),
  );
  if (nodes.length === 0) return;

  if (target === 'head') {
    const head = document.head;
    // head のなるべく上に入れる（既存の先頭ノードの前に挿入）
    const first = head.firstChild;
    nodes.forEach((n) => head.insertBefore(n, first));
  } else {
    const body = document.body;
    // body 直下の先頭に入れる（逆順 insert で元の順序を保つ）
    const first = body.firstChild;
    nodes.reverse().forEach((n) => body.insertBefore(n, first));
  }
}

export interface GoogleTagInput {
  headHtml?: string | null;
  bodyHtml?: string | null;
}

/**
 * 公開ページで Google タグを注入する React フック。
 * headHtml / bodyHtml が変わるたびに再注入し、unmount 時に除去する。
 */
export function useGoogleTag(tag: GoogleTagInput | null | undefined): void {
  const headHtml = tag?.headHtml ?? '';
  const bodyHtml = tag?.bodyHtml ?? '';

  useEffect(() => {
    injectTagHtml(headHtml, 'head', 'gtag-head');
    injectTagHtml(bodyHtml, 'body', 'gtag-body');
    return () => {
      removeInjected('gtag-head');
      removeInjected('gtag-body');
    };
  }, [headHtml, bodyHtml]);
}

/**
 * グローバル既定とランディング個別タグから、実際に注入すべきタグを解決する。
 * 優先順位:
 *   1. 個別タグが disabled === true → 何も注入しない（既定も無効化＝オプトアウト）
 *   2. 個別タグの head/body いずれかが非空 → 個別を優先（上書き）
 *   3. それ以外でグローバル既定が有効 → 既定
 *   4. どれにも該当しなければ何も注入しない
 */
export function resolveEffectiveTag(
  landingTag:
    | { headHtml?: string; bodyHtml?: string; disabled?: boolean }
    | null
    | undefined,
  globalDefault:
    | { enabled: boolean; headHtml: string; bodyHtml: string }
    | null
    | undefined,
): GoogleTagInput {
  // オプトアウト: この LP には既定タグも個別タグも一切注入しない。
  if (landingTag?.disabled) {
    return { headHtml: '', bodyHtml: '' };
  }
  const lHead = (landingTag?.headHtml ?? '').trim();
  const lBody = (landingTag?.bodyHtml ?? '').trim();
  if (lHead || lBody) {
    return { headHtml: lHead, bodyHtml: lBody };
  }
  if (globalDefault?.enabled) {
    return { headHtml: globalDefault.headHtml, bodyHtml: globalDefault.bodyHtml };
  }
  return { headHtml: '', bodyHtml: '' };
}
